/**
 * Import API Service
 * Handles bookmark import functionality
 */

import APP_CONFIG from '../config/appConfig';

const API_BASE_URL = APP_CONFIG.API.BASE_URL;

/**
 * Import bookmarks from parsed bookmark array
 * @param {Array} bookmarks - Array of parsed bookmark objects
 * @returns {Promise<Object>} Import result with statistics
 */
export const importBookmarks = async (bookmarks) => {
  try {
    // 🔒 SECURITY FIX: Strict authentication required
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      throw new Error('Authentication required. Please log in first to import bookmarks.');
    }

    // Security: Never log tokens or sensitive auth data

    const response = await fetch(`${API_BASE_URL}/import/bookmarks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(bookmarks),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // If invalid token error and we have a stored token, clear it and retry
      if (errorData.message === 'Import fehlgeschlagen: Invalid token' && localStorage.getItem('authToken')) {
        localStorage.removeItem('authToken');
        // Retry with dev-token-1
        return importBookmarks(bookmarks);
      }
      
      if (response.status === 401 || response.status === 403) {
        throw new Error('Session abgelaufen - Bitte melden Sie sich erneut an');
      }
      
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('❌ Import API Error:', error);
    throw error;
  }
};

/**
 * Import bookmarks directly from Chrome HTML file
 * @param {File} file - The HTML file from Chrome export
 * @returns {Promise<Object>} Import result with statistics
 */
export const importChromeFile = async (file) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Nicht authentifiziert');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/import/chrome`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Chrome Import API Error:', error);
    throw error;
  }
};

/**
 * Get import history for current user
 * @returns {Promise<Array>} Array of import history entries
 */
export const getImportHistory = async () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Nicht authentifiziert');
    }

    const response = await fetch(`${API_BASE_URL}/import/history`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Import History API Error:', error);
    throw error;
  }
};

/**
 * Get import statistics for current user
 * @returns {Promise<Object>} Import statistics
 */
export const getImportStats = async () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Nicht authentifiziert');
    }

    const response = await fetch(`${API_BASE_URL}/import/stats`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Import Stats API Error:', error);
    throw error;
  }
};

/**
 * Delete import history entry
 * @param {number} id - Import history entry ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteImportHistory = async (id) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Nicht authentifiziert');
    }

    const response = await fetch(`${API_BASE_URL}/import/history/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Delete Import History API Error:', error);
    throw error;
  }
};

/**
 * Save imported bookmarks to user-specific import directory
 * @param {string} email - User email
 * @param {Array} bookmarks - Imported bookmarks
 * @param {string} fileName - Original file name
 * @returns {Promise<string>} File path where bookmarks were saved
 */
export const saveImportToDirectory = async (email, bookmarks, fileName) => {
  try {
    // Create user-specific import directory path
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const importFileName = `${sanitizedEmail}_${timestamp}_${fileName}`;
    
    // For now, save to localStorage as backup
    // In a real deployment, this would be handled by the backend
    const importData = {
      fileName: importFileName,
      originalFileName: fileName,
      userEmail: email,
      importDate: new Date().toISOString(),
      bookmarksCount: bookmarks.length,
      bookmarks: bookmarks
    };
    
    localStorage.setItem(`import_${importFileName}`, JSON.stringify(importData));
    
    console.log(`📁 Import saved locally: ${importFileName}`);
    return `import/${sanitizedEmail}/${importFileName}`;
    
  } catch (error) {
    console.error('Error saving import to directory:', error);
    throw new Error('Fehler beim Speichern der Import-Datei: ' + error.message);
  }
};

/**
 * Get saved imports from user directory
 * @param {string} email - User email
 * @returns {Promise<Array>} Array of saved import files
 */
export const getSavedImports = async (email) => {
  try {
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const savedImports = [];
    
    // Search localStorage for user imports
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`import_${sanitizedEmail}_`)) {
        const importData = JSON.parse(localStorage.getItem(key));
        savedImports.push({
          key: key,
          fileName: importData.fileName,
          originalFileName: importData.originalFileName,
          importDate: importData.importDate,
          bookmarksCount: importData.bookmarksCount
        });
      }
    }
    
    // Sort by import date, newest first
    savedImports.sort((a, b) => new Date(b.importDate) - new Date(a.importDate));
    
    return savedImports;
    
  } catch (error) {
    console.error('Error getting saved imports:', error);
    return [];
  }
};

export default {
  importBookmarks,
  importChromeFile,
  getImportHistory,
  getImportStats,
  deleteImportHistory,
  saveImportToDirectory,
  getSavedImports
};